import { push } from 'react-router-redux';
import SubmissionError from 'redux-form/lib/SubmissionError';
import { CHANGE } from 'redux-form/lib/actionTypes';
import unescaper from 'anchor-server-common/utilities/unescaper';
import { VALID_USER_EMAIL_REGEX } from 'anchor-server-common/utilities/user/constants';
import { unsetAndRedirectUser, fetchUserVerificationState } from './user';
import AnchorAPI from './modules/AnchorAPI';
import AnchorAPIError from './modules/AnchorAPI/AnchorAPIError';

export const RECEIVE_PODCAST_METADATA =
  '@@podcastEditor/RECEIVE_PODCAST_METADATA';
export const RECEIVE_PODCAST_METADATA_FETCH_REQUEST =
  '@@podcastEditor/RECEIVE_PODCAST_METADATA_FETCH_REQUEST';
export const PODCAST_METADATA_EDIT_FORM = 'podcastMetadataEditor';
export const PODCAST_METADATA_EDIT_SHORT_FORM = 'podcastMetadataShortEditor';
export const RECEIVE_LOCAL_IMAGE_EDIT =
  '@@podcastEditor/RECEIVE_LOCAL_IMAGE_EDIT';
export const RECEIVE_PODCAST_IMAGE = '@@podcastEditor/RECEIVE_PODCAST_IMAGE';
export const RECEIVE_PODCAST_CATEGORIES =
  '@@podcastEditor/RECEIVE_PODCAST_CATEGORIES';
export const RECEIVE_SOCIAL_URLS = '@@podcastEditor/RECEIVE_SOCIAL_URLS';

const emptyArray = [];
const emptyObject = {};

// TODO (bui): fetching default state should not be true
const initialState = {
  isFetchingMetadata: true,
  authorName: null,
  userEmail: null,
  userBioUrl: null,
  feedUrl: null,
  podcastImage: null,
  podcastImageFull: null,
  editedImage: null,
  isExplicit: false,
  itunesCategory: null,
  language: null,
  listenerSupportCustomMessage: null,
  podcastDescription: null,
  podcastName: null,
  safePodcastDescription: null,
  vanitySlug: '',
  vanitySlugIsPristine: true,
  hasAnchorBranding: true,
  doEnableListenerSupportLinkInEpisodes: false,
  isPublicCallinShownOnWeb: true,
  isPublicCallinShownFromRSS: false,
  isUserEmailInRss: false,
  podcastCategoryOptions: emptyArray,
  profileHeaderColor: '',
  userSocialUrls: emptyObject,
};

// reducers

// eslint-disable-next-line import/no-default-export
export default function reducer(state = initialState, action) {
  switch (action.type) {
    case RECEIVE_PODCAST_IMAGE: {
      return {
        ...state,
        podcastImage: action.payload.podcastImage,
      };
    }
    case CHANGE: {
      const {
        payload,
        meta: { form, field },
      } = action;
      const { vanitySlug } = state;
      if (
        (form === PODCAST_METADATA_EDIT_FORM ||
          form === PODCAST_METADATA_EDIT_SHORT_FORM) &&
        field === 'vanitySlug'
      ) {
        return {
          ...state,
          vanitySlugIsPristine: payload === vanitySlug,
        };
      }
      return state;
    }
    case RECEIVE_PODCAST_METADATA_FETCH_REQUEST: {
      return {
        ...state,
        isFetchingMetadata: action.payload.isFetchingMetadata,
      };
    }
    case RECEIVE_PODCAST_METADATA: {
      const {
        authorName,
        doEnableListenerSupportLinkInEpisodes,
        feedUrl,
        hasAnchorBranding,
        isExplicit,
        itunesCategory,
        language,
        listenerSupportCustomMessage,
        podcastDescription,
        podcastImage,
        podcastImageFull,
        podcastName,
        safePodcastDescription,
        userEmail,
        userBioUrl,
        vanitySlug,
        doHideDefaultAdSlotsInEpisodes,
        isPublicCallinShownOnWeb,
        isPublicCallinShownFromRSS,
        isUserEmailInRss,
        hasInternationalSponsorships,
        profileHeaderColor,
        contactabilityStatus,
        isMusicAndTalkFastTrackEnabled,
      } = action.payload;
      return {
        ...state,
        authorName: unescaper(authorName),
        doEnableListenerSupportLinkInEpisodes,
        hasAnchorBranding,
        feedUrl,
        isExplicit,
        isFetchingMetadata: false,
        itunesCategory,
        language,
        listenerSupportCustomMessage,
        podcastName: unescaper(podcastName),
        podcastImage,
        podcastImageFull,
        podcastDescription: unescaper(podcastDescription),
        safePodcastDescription: unescaper(safePodcastDescription),
        userEmail: unescaper(userEmail),
        userBioUrl: unescaper(userBioUrl),
        vanitySlug,
        doHideDefaultAdSlotsInEpisodes,
        isPublicCallinShownOnWeb,
        isPublicCallinShownFromRSS,
        isUserEmailInRss,
        hasInternationalSponsorships,
        profileHeaderColor,
        contactabilityStatus,
        isMusicAndTalkFastTrackEnabled,
      };
    }
    case RECEIVE_LOCAL_IMAGE_EDIT: {
      return {
        ...state,
        editedImage: action.payload.imageUrl,
      };
    }
    case RECEIVE_PODCAST_CATEGORIES: {
      return {
        ...state,
        podcastCategoryOptions: action.payload.categories,
      };
    }
    case RECEIVE_SOCIAL_URLS: {
      return {
        ...state,
        userSocialUrls: action.payload.userSocialUrls,
      };
    }
    default:
      return state;
  }
}

// action creators

function receivePodcastMetadataFetchRequest(fetching) {
  return {
    type: RECEIVE_PODCAST_METADATA_FETCH_REQUEST,
    payload: {
      isFetchingMetadata: fetching,
    },
  };
}

function receivePodcastCategories(categories) {
  return {
    type: RECEIVE_PODCAST_CATEGORIES,
    payload: {
      categories,
    },
  };
}

function receiveSocialUrls(userSocialUrls) {
  return {
    type: RECEIVE_SOCIAL_URLS,
    payload: {
      userSocialUrls,
    },
  };
}

export function receivePodcastImage(imageUrl) {
  return {
    type: RECEIVE_PODCAST_IMAGE,
    payload: {
      podcastImage: imageUrl,
    },
  };
}

export function receivePodcastMetadata(podcastMetadata) {
  return {
    type: RECEIVE_PODCAST_METADATA,
    payload: podcastMetadata,
  };
}

export function receiveLocalUploadedImage(imageUrl) {
  return {
    type: RECEIVE_LOCAL_IMAGE_EDIT,
    payload: { imageUrl },
  };
}

// thunks

export function fetchPodcastCategories() {
  return dispatch => {
    AnchorAPI.fetchPodcastCategories().then(res =>
      dispatch(
        receivePodcastCategories(
          flattenPodcastCategories(res.podcastCategoryOptions)
        )
      )
    );
  };
}

export function fetchSocialUrls(stationId) {
  return dispatch => {
    AnchorAPI.fetchSocialUrls({ stationId }).then(res => {
      const payload = res.userSocialUrls.reduce(
        (acc, platform) => ({
          [platform.type]: platform.username,
          ...acc,
        }),
        {}
      );
      dispatch(receiveSocialUrls(payload));
    });
  };
}

export function fetchMyPodcastMetadata() {
  return (dispatch, getState) => {
    dispatch(receivePodcastMetadataFetchRequest(true));
    return fetch('/api/podcast/metadata', {
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        }
        return response;
      })
      .then(responseJson => {
        if (responseJson.status && responseJson.status !== 200) {
          if (responseJson.status === 401) {
            // not logged in
            return dispatch(unsetAndRedirectUser());
          }
          return responseJson;
        }
        if (responseJson && responseJson.isFirstTimeCreator) {
          // no metadata; first time podcast creator
          const {
            onboarding,
            user: { user },
          } = getState();
          if (!onboarding.image) {
            // TODO: What text? Move this until submission? (comes as 'null')
          }
          dispatch(
            receivePodcastMetadata({
              ...responseJson,
              ...preFillPodcastForCreation({
                podcast: responseJson,
                user,
              }),
            })
          );
          return responseJson;
        }
        dispatch(receivePodcastMetadata(responseJson));
        return responseJson;
      })
      .then(responseJson => {
        dispatch(receivePodcastMetadataFetchRequest(false));
        return responseJson;
      })
      .catch(() => dispatch(receivePodcastMetadataFetchRequest(false)));
  };
}

// important that this returns a promise to the form submit handler
export function submitUpdatePodcastMetadataShortForm(data) {
  submitUpdatePodcastMetadataForm(data, true);
}

/**
 * @name submitUpdatePodcastMetadataForm
 * @typedef {Object} FormData - Settings Form state in key/value pair ( `{ [key]: any }` )
 * @param {FormData} formData - the user's metadata
 */
export function submitUpdatePodcastMetadataForm(formData) {
  return (dispatch, getState) => {
    const {
      podcast,
      podcastEditor,
      user: { user },
    } = getState();
    const { podcastUrlDictionary } = podcast;
    const { vanitySlug } = podcastEditor;
    const { userId } = user;
    const {
      doShowDefaultAdSlotsInEpisodes,
      facebookSocialUsername,
      instagramSocialUsername,
      youtubeSocialUsername,
      ...restData
    } = formData;
    const data = {
      ...restData,
      doHideDefaultAdSlotsInEpisodes: !doShowDefaultAdSlotsInEpisodes,
    };
    const socialUrls = {
      facebookSocialUsername,
      instagramSocialUsername,
      youtubeSocialUsername,
    };
    if (vanitySlug === data.vanitySlug) {
      delete data.vanitySlug;
    }
    const { externalLinks } = data;
    if (externalLinks) {
      delete externalLinks.anchor; // always read-only
    }
    const newExternalLinks = {};
    for (const k in externalLinks) {
      if (externalLinks[k] === podcastUrlDictionary[k]) {
        delete externalLinks[k];
      } else if (externalLinks[k] === '') {
        newExternalLinks[k] = null;
      } else {
        newExternalLinks[k] = externalLinks[k];
      }
    }
    const putData = Object.assign({}, data, {
      externalLinks: newExternalLinks,
    });
    return AnchorAPI.updatePodcastMetadataDeprecated(putData)
      .then(() => AnchorAPI.updateSocialUrls({ userId, socialUrls }))
      .then(() => {
        if (podcastEditor.userEmail !== restData.userEmail) {
          dispatch(fetchUserVerificationState());
        }
        dispatch(fetchMyPodcastMetadata());
      })
      .catch(err => {
        switch (err.constructor) {
          case AnchorAPIError:
            const apiError = err;
            const { response } = apiError;
            const { status } = response;
            switch (status) {
              case 400:
                return response.text().then(responseText => {
                  window.scrollTo(0, 0);
                  throw new SubmissionError({ _error: responseText });
                });
              case 403:
                dispatch(push('/404'));
                return;
              case 401:
                dispatch(unsetAndRedirectUser());
                return;
              default:
                throw err; // We didn't expect this
            }
          default:
            throw err; // We didn't expect this
        }
      });
  };
}

// misc

export function flattenPodcastCategories(podcastCategories) {
  const categoryOptions = [];
  podcastCategories.forEach(category => {
    const { display, value } = category;
    categoryOptions.push({
      label: display,
      value,
    });
    if (category.subCategories && category.subCategories.length) {
      category.subCategories.forEach(subCategory => {
        categoryOptions.push({
          // note - styling <option> is only supported in Firefox / not recommended
          // <optgroup> are not selectable, so simply indent using non-breaking space
          // \u00a0 - &nbsp;
          label: `\u00a0\u00a0${subCategory.display}`,
          value: subCategory.value,
        });
      });
    }
  });
  return categoryOptions;
}

function preFillPodcastForCreation({ podcast, user }) {
  const { vanitySlug } = podcast;
  return {
    authorName: user.name,
    vanitySlug,
  };
}

function validateEmail(email) {
  return VALID_USER_EMAIL_REGEX.test(email);
}

export const validVanitySlugRegex = new RegExp('^[a-zA-Z0-9-]+$');

export function validate(values) {
  const { userEmail, vanitySlug } = values;
  const errors = {};
  if (!userEmail || !validateEmail(userEmail)) {
    errors.userEmail = 'Must provide a valid email address.';
  }
  if (!vanitySlug || !validVanitySlugRegex.test(vanitySlug)) {
    errors.vanitySlug = 'Must provide a valid URL.';
  }
  return errors;
}
